import React, { useState, useEffect } from 'react';
import {
  Activity,
  MapPin,
  Clock,
  Navigation,
  AlertCircle,
  Zap,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { TripCard } from './TripCard';
import { RouteProgress } from './RouteDisplay';
import { TRIP_STATUS } from '../../store/tripSlice';

/**
 * ActiveTripMonitor Component
 * 
 * A specialized component for monitoring active trips with real-time updates,
 * progress tracking, and status indicators.
 */
export function ActiveTripMonitor({ 
  trips, 
  onViewDetails,
  onCompleteTrip,
  onCancelTrip,
  onRefresh,
  className = '' 
}) {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Simulate real-time updates
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // In a real app, this would trigger data refresh
      onRefresh?.();
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, onRefresh]);

  // Simulate connection status
  useEffect(() => {
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getElapsedTime = (startTime) => {
    if (!startTime) return null;
    const elapsed = Math.floor((new Date() - new Date(startTime)) / 60000);
    const hours = Math.floor(elapsed / 60);
    const minutes = elapsed % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const calculateProgress = (trip) => {
    if (trip.status !== TRIP_STATUS.ONGOING || !trip.startedAt) return 0;
    
    const elapsed = (new Date() - new Date(trip.startedAt)) / 60000; // minutes
    const estimated = trip.estimatedDuration || 30;
    return Math.min((elapsed / estimated) * 100, 95); // Cap at 95% until completed
  };

  const getUrgentTrips = () => {
    return trips.filter(trip => {
      if (trip.status === TRIP_STATUS.ONGOING && trip.startedAt) {
        const elapsed = (new Date() - new Date(trip.startedAt)) / 60000;
        const estimated = trip.estimatedDuration || 30;
        return elapsed > estimated * 1.2; // 20% over estimated time
      }
      if (trip.status === TRIP_STATUS.ASSIGNED && trip.assignedAt) {
        const elapsed = (new Date() - new Date(trip.assignedAt)) / 60000;
        return elapsed > 10; // Assigned for more than 10 minutes
      }
      return false;
    });
  };

  const urgentTrips = getUrgentTrips();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Status Header */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-sm font-medium text-gray-900">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Last update: {lastUpdate.toLocaleTimeString()}</span>
            </div>

            <div className="flex items-center space-x-2">
              <Activity className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">
                {trips.length} active trips
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-2 rounded-lg transition-colors ${
                autoRefresh 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600'
              }`}
              title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
            >
              {autoRefresh ? <Zap className="w-4 h-4" /> : <Wifi className="w-4 h-4" />}
            </button>
            
            {urgentTrips.length > 0 && (
              <Badge variant="danger" className="animate-pulse">
                {urgentTrips.length} urgent
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Urgent Trips Alert */}
      {urgentTrips.length > 0 && (
        <Card className="p-4 border-orange-200 bg-orange-50">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                Attention Required
              </h3>
              <p className="text-sm text-orange-700 mt-1">
                {urgentTrips.length} trip{urgentTrips.length > 1 ? 's' : ''} require immediate attention
              </p>
              <div className="mt-2 space-y-1">
                {urgentTrips.slice(0, 3).map(trip => (
                  <div key={trip.id} className="text-xs text-orange-600">
                    Trip #{trip.id.slice(-6)} - {trip.passenger.name}
                    {trip.status === TRIP_STATUS.ONGOING && trip.startedAt && (
                      <span className="ml-2">
                        (Running {getElapsedTime(trip.startedAt)})
                      </span>
                    )}
                  </div>
                ))}
                {urgentTrips.length > 3 && (
                  <div className="text-xs text-orange-600">
                    +{urgentTrips.length - 3} more trips
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Active Trips Grid */}
      {trips.length === 0 ? (
        <Card className="p-12 text-center">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Active Trips
          </h3>
          <p className="text-gray-500">
            All trips are currently completed or no new requests have been made.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {trips.map((trip) => (
            <EnhancedTripCard
              key={trip.id}
              trip={trip}
              progress={calculateProgress(trip)}
              isUrgent={urgentTrips.some(urgentTrip => urgentTrip.id === trip.id)}
              onViewDetails={onViewDetails}
              onCompleteTrip={onCompleteTrip}
              onCancelTrip={onCancelTrip}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * EnhancedTripCard Component
 * 
 * An enhanced version of TripCard with real-time features
 */
function EnhancedTripCard({ 
  trip, 
  progress = 0, 
  isUrgent = false, 
  onViewDetails, 
  onCompleteTrip, 
  onCancelTrip 
}) {
  const getElapsedTime = (startTime) => {
    if (!startTime) return null;
    const elapsed = Math.floor((new Date() - new Date(startTime)) / 60000);
    return `${elapsed} min`;
  };

  return (
    <div className={`relative ${isUrgent ? 'ring-2 ring-orange-400' : ''}`}>
      {isUrgent && (
        <div className="absolute -top-2 -right-2 z-10">
          <Badge variant="danger" size="sm" className="animate-pulse">
            Urgent
          </Badge>
        </div>
      )}
      
      <TripCard
        trip={trip}
        onViewDetails={onViewDetails}
        onCompleteTrip={onCompleteTrip}
        onCancelTrip={onCancelTrip}
        className={isUrgent ? 'border-orange-200' : ''}
      />

      {/* Progress Overlay for Ongoing Trips */}
      {trip.status === TRIP_STATUS.ONGOING && (
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-3 border">
            <RouteProgress
              pickup={trip.pickup}
              dropoff={trip.dropoff}
              currentLocation={trip.currentLocation}
              progress={progress}
            />
            {trip.startedAt && (
              <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                <span>Elapsed: {getElapsedTime(trip.startedAt)}</span>
                <span>Est: {trip.estimatedDuration}m</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * TripStatusSummary Component
 * 
 * A summary widget showing overall active trip statistics
 */
export function ActiveTripsSummary({ trips, className = '' }) {
  const ongoingTrips = trips.filter(trip => trip.status === TRIP_STATUS.ONGOING);
  const assignedTrips = trips.filter(trip => trip.status === TRIP_STATUS.ASSIGNED);
  const requestedTrips = trips.filter(trip => trip.status === TRIP_STATUS.REQUESTED);

  const averageProgress = ongoingTrips.length > 0 
    ? ongoingTrips.reduce((sum, trip) => {
        const elapsed = trip.startedAt ? (new Date() - new Date(trip.startedAt)) / 60000 : 0;
        const estimated = trip.estimatedDuration || 30;
        return sum + Math.min((elapsed / estimated) * 100, 95);
      }, 0) / ongoingTrips.length
    : 0;

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      <Card className="p-4 text-center">
        <div className="text-2xl font-bold text-blue-600">{ongoingTrips.length}</div>
        <div className="text-sm text-gray-600">In Progress</div>
      </Card>
      
      <Card className="p-4 text-center">
        <div className="text-2xl font-bold text-yellow-600">{assignedTrips.length}</div>
        <div className="text-sm text-gray-600">Assigned</div>
      </Card>
      
      <Card className="p-4 text-center">
        <div className="text-2xl font-bold text-gray-600">{requestedTrips.length}</div>
        <div className="text-sm text-gray-600">Requested</div>
      </Card>
      
      <Card className="p-4 text-center">
        <div className="text-2xl font-bold text-green-600">{averageProgress.toFixed(0)}%</div>
        <div className="text-sm text-gray-600">Avg Progress</div>
      </Card>
    </div>
  );
}
